import * as Sentry from '@sentry/react';
import { Profile, ProfileFieldProps } from '../components/types';
import { Amplitude } from '../amplitude/amplitude';

export const validateMinLength = (password: string) => password.length >= 8;

export const validateUpperCase = (password: string) =>
  new RegExp('[A-Z]').test(password);

export const validateLowerCase = (password: string) =>
  new RegExp('[a-z]').test(password);

export const validateDigit = (password: string) =>
  new RegExp('[0-9]').test(password);

// Matches a string that contains at least one non-word character or _. (_ is considered a word character).
export const validateSpecialCharacter = (password: string) =>
  new RegExp('[\\W_]').test(password);

// Authorize dash, apostrophe, space, and alphabet with accents: -' a-zA-ZÀ-ÿ\u00f1\u00d1
// Chinese & Japanese: \u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f
// Korean:  \u3131-\uD79D
export const validateName = (name: string) =>
  !new RegExp(
    /([^-' a-zA-ZÀ-ÿ\u00f1\u00d1\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f\u3131-\uD79D])/,
  ).test(name);

export const validateConfirmPassword = (
  password: string,
  confirmPassword: string,
) => password == '' || password == confirmPassword;

// Validates that the password meets all the requirements.
export const validatePassword = (password: string, confirmPassword: string) => {
  return (
    validateMinLength(password) &&
    validateUpperCase(password) &&
    validateLowerCase(password) &&
    validateDigit(password) &&
    validateSpecialCharacter(password) &&
    validateConfirmPassword(password, confirmPassword)
  );
};

// Validates that the profile has all the mandatory fields filled.
export const validateProfile = (profile: ProfileFieldProps['profile']) => {
  return (
    profile.token != '' &&
    profile.email != '' &&
    (profile.sso ||
      validatePassword(profile.password, profile.confirmPassword)) &&
    profile.firstName !== undefined &&
    profile.firstName.trim() != '' &&
    validateName(profile.firstName) &&
    profile.country != undefined &&
    profile.phoneNumber != undefined &&
    profile.phoneNumber != '' &&
    profile.country != '' &&
    profile.termsOfUse
  );
};

type SetupProfile = {
  token: string;
  email: string;
  country: string;
  hasExistingProfile: boolean;
  errorCode?: string;
  sso?: boolean;
  firstName?: string;
  lastName?: string;
  phoneCode?: string;
  phoneNumber?: string;
  apiUrl?: string;
};

/// Gets the api url from the environment variable and the region.
///
/// For production, env is 'production' and region is 'frc' or 'sea'. For
/// staging, env is 'staging' and region is always 'frc'. For local, there is no
/// env and region defined.
export const getApiUrl = (): string => {
  // Gets query parameters from the URL.
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const env = getEnv();
  const region = params['region'];
  if (env == 'production') return `https://api.${region}.lite.novade.net`;
  if (env == 'staging') return `https://api.${region}.staging.lite.novade.net`;
  return 'http://localhost:3000';
};

/// Gets the novade url from the environment variable.
///
/// For production, env is 'production'. For staging, env is 'staging'. For
/// local, there is no env defined.
export const getNovadeUrl = (): string => {
  const env = getEnv();
  if (env == 'production' || env == '') return 'https://app.novade.net';
  if (env == 'staging') return 'https://app-staging.novade.net';
  return 'http://localhost:1234';
};

/// Gets the app url from the environment variable.
///
/// For production, env is 'production'. For staging, env is 'staging'. For
/// local, there is no env defined.
export const getAppUrl = (): string => {
  const env = getEnv();
  if (env == 'production' || env == '') return 'https://signup.app.novade.net';
  if (env == 'staging') return 'https://signup.app-staging.novade.net';
  return 'http://localhost:1234';
};

/// Gets the region from the environment variable and the api url.
export const getRegion = (env: string, apiUrl: string): string => {
  if (env == 'production') {
    const params = apiUrl.split('.');
    const region = params[1];
    return region;
  }
  return 'frc';
};

/// Gets the environment variable.
export const getEnv = (): string => {
  const env = process.env.REACT_APP_ENV;
  return env ?? '';
};

// Decodes token.
export const decodeToken = (token: string) => {
  try {
    const parts = token.split('.');

    // Ensures there are three parts.
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    // Decodes the base64url-encoded payload.
    const decodedToken = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    );

    return decodedToken;
  } catch (error) {
    Sentry.captureException(error);
    return {};
  }
};

type SignUpFinalOrAcceptInviteResponse = {
  // Common fields between DbCreationResult and AcceptUserInviteResponse
  error?: string;
  exists?: boolean;
  token?: string;

  // AcceptUserInviteResponse fields
  userID?: string;
  sso?: boolean;
  firstName?: string;
  lastName?: string;
  phoneCode?: string;
  phoneNumber?: string;
};

// Gets the setup profile payload from the JWT token.
export const fetchSetupProfile = async (
  isCreatingWorkspace: boolean,
): Promise<SetupProfile> => {
  // Gets query parameters from the URL.
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());

  const token = params['token'];
  const apiUrl = getApiUrl();
  const path = isCreatingWorkspace ? 'sign-up-final' : 'accept-invite';
  const response = await fetch(`${apiUrl}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token: token,
    }),
  });

  const { email, country } = decodeToken(token);
  const responseBody: SignUpFinalOrAcceptInviteResponse = await response.json();

  let errorCode;
  if (response.status != 201 || responseBody['error'] != null) {
    errorCode =
      [401, 500].includes(response.status) && responseBody['error'] == null
        ? 'DEFAULT'
        : (responseBody['error'] as string);
    return {
      token,
      email: email ?? '',
      country: country ?? '',
      errorCode,
      hasExistingProfile: false,
    };
  }

  const baseResult = {
    token,
    email: email ?? '',
    country: country ?? '',
    errorCode,
    sso: responseBody.sso,
    firstName: responseBody.firstName ?? '',
    lastName: responseBody.lastName,
    phoneCode: responseBody.phoneCode,
    phoneNumber: responseBody.phoneNumber,
    apiUrl,
  };
  if (responseBody.exists) {
    return {
      ...baseResult,
      hasExistingProfile: true,
    };
  }

  if (!responseBody.token) {
    return {
      ...baseResult,
      errorCode: 'DEFAULT',
      hasExistingProfile: false,
    };
  }

  const setupProfile = decodeToken(responseBody.token);
  return {
    ...baseResult,
    email: setupProfile['email'],
    country: setupProfile['country'],
    hasExistingProfile: false,
  };
};

// Get the current country from the device location if the user has given permission.
export const fetchDeviceLocation = async (): Promise<string | null> => {
  try {
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const data = await ipResponse.json();

    const url = getApiUrl();
    const response = await fetch(`${url}/user-location`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userIPAddress: data.ip,
      }),
    });

    const responseBody = await response.json();
    if (responseBody['countryCode'] != null) {
      return responseBody['countryCode'];
    }
    return null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export type ErrorMessage = {
  titleID: string;
  subtitleID: string;
};

// Gets the error message ID from the error code and the path.
export const getSetUpProfileErrorMessageID = (
  errorCode: string,
  pathname: string,
): ErrorMessage => {
  let errorID;
  switch (errorCode) {
    case 'EXPIRED_TOKEN':
    case 'DELETED_WORKSPACE':
    case 'INVALID_TOKEN':
      if (pathname.includes('sign-up-final')) {
        errorID = 'sign_up_invalid';
      } else {
        errorID = 'invite_invalid';
      }
      break;
    case 'TOKEN_ALREADY_ACCEPTED':
      if (pathname.includes('sign-up-final')) {
        errorID = 'sign_up_accepted';
      } else {
        errorID = 'invite_accepted';
      }
      break;
    default:
      errorID = 'default';
  }
  return {
    titleID: `error.${errorID}.title`,
    subtitleID: `error.${errorID}.subtitle`,
  };
};

// Gets the locale from the browser. It is sliced to remove the region code.
// Example: en-US -> en, fr-FR -> fr
export const getLocale = (): string => {
  const language = navigator.language;
  const [countryCode, regionCode] = language.split('-');
  if (countryCode === 'zh') {
    return ['HK', 'TW', 'MO'].includes(regionCode) ? 'zh-Hant' : 'zh-Hans';
  }
  return countryCode;
};

const getAmplitudeInfo = (profile: Profile | undefined) => {
  const { email, country, token } = profile || {};
  const { id, databaseID } = decodeToken(token ?? '');
  return { id, databaseID, email, country };
};

// Gets the device ID from Amplitude.
export const getDeviceId = (profile: Profile | undefined) => {
  const { id } = getAmplitudeInfo(profile);
  const deviceID = Amplitude.getInstance(id).getDeviceId();
  return deviceID ? `device-id=${deviceID}` : '';
};

// Tracks the 'gotoNovadeLiteClick' event in Amplitude.
export const trackGotoNovadeLiteEvent = (profile: Profile | undefined) => {
  const { id, databaseID, email, country } = getAmplitudeInfo(profile);
  Amplitude.getInstance(id).track('gotoNovadeLiteClick', {
    email: email ?? '',
    country: country ?? '',
    databaseID: databaseID ?? '',
  });
};
